import { z } from 'zod';
import { Condition } from './conditions.js';
/**
 * Routes: the branches of a journey (tracker task 85, decision 0056). A stage lists routes; each follows
 * one of its questions and, once that question is answered and `when` holds, skips ahead:
 *
 *   { after: 'BR-01', when: { question: 'BR-01', op: 'eq', value: 'no' },
 *     then: { action: 'skip_questions', questions: ['BR-04', 'BR-07'] }, skipped_as: 'not_in_place',
 *     label: 'No automatic backups' }
 *
 * Actions:
 * - `goto`: on to a later question of the same stage, skipping those in between;
 * - `skip_questions`: these later questions (in this stage or a stage that waits for it);
 * - `end_stage`: the rest of this stage;
 * - `skip_stages`: whole stages that wait for this one;
 * - `end_journey`: the rest of this stage and every stage that waits for it.
 *
 * What a skipped question counts as:
 * - `not_asked`: it doesn't apply, like a question hidden by its conditions (no website, no web controls);
 * - `not_in_place`: it applies and counts as answered with its "not in place" answer (no backups, so no
 *   tested restores), so skipping never improves a score.
 *
 * Routes only go forward, so a journey can't loop. Several routes after one question are tried in order
 * and the first that holds is taken. Routes are compiled into each skipped question's `skipped_by` when a
 * bundle is built (`compileFlow`), so the form, chat, API, rules and scoring all follow the same path.
 */
export const ROUTE_ACTIONS = ['goto', 'skip_questions', 'end_stage', 'skip_stages', 'end_journey'];
const Key = z.string().min(1).max(64);
export const RouteAction = z.discriminatedUnion('action', [
    z.strictObject({ action: z.literal('goto'), question: Key }),
    z.strictObject({ action: z.literal('skip_questions'), questions: z.array(Key).min(1).max(200) }),
    z.strictObject({ action: z.literal('end_stage') }),
    z.strictObject({ action: z.literal('skip_stages'), stages: z.array(Key).min(1).max(50) }),
    z.strictObject({ action: z.literal('end_journey') }),
]);
export const SKIPPED_AS = ['not_asked', 'not_in_place'];
export const Route = z.strictObject({
    /** The question this route follows, in the same stage. */
    after: Key,
    /** Taken when this holds once `after` is answered. Omitted: always. */
    when: Condition.optional(),
    then: RouteAction,
    skipped_as: z.enum(SKIPPED_AS).default('not_asked'),
    /** Shown to people: why questions were skipped ("No automatic backups"). */
    label: z.string().trim().min(1).max(200),
});
/**
 * One way a question can be skipped, compiled from a route. Not authored: `compileFlow` replaces it whenever
 * a bundle is built. `when` is the whole test (the source answered, the route's condition, and no earlier
 * route after the same question taken).
 */
export const SkipGuard = z.strictObject({
    route: z.string().min(1).max(80),
    from: Key,
    label: z.string().max(200),
    when: Condition,
    as: z.enum(SKIPPED_AS),
    /** not_in_place: the answer it counts as. */
    value: z.string().max(64).optional(),
});
/** A route's reference, as questions and stages carry it. */
export const routeId = (stage, index) => `${stage}#${index}`;
/** A short reading of what a route does, for lists and explanations. */
export function describeRoute(r, name = (s) => s) {
    const as = r.skipped_as === 'not_in_place' ? ', counted as not in place' : '';
    switch (r.then.action) {
        case 'goto':
            return `go on to ${r.then.question}${as}`;
        case 'skip_questions':
            return `skip ${r.then.questions.join(', ')}${as}`;
        case 'end_stage':
            return `skip the rest of this stage${as}`;
        case 'skip_stages':
            return `skip ${r.then.stages.map(name).join(', ')}${as}`;
        case 'end_journey':
            return `end the assessment here${as}`;
    }
}
//# sourceMappingURL=routes.js.map